import { faCalendar, faCheck, faFolder } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Collapse } from '@mui/material';
import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router';

import { Button, Link } from 'Components/Buttons/Buttons';
import { OverflowItem, OverflowMenu, OverflowMenuProps } from 'Components/Buttons/OverflowMenu';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { TableCell, TableOverflowCell, TableRow } from 'Components/Table/Table/Table';
import { TagListing } from 'Components/Tags/TagListing/TagListing';
import { Text } from 'Components/Text/Text';
import { UserDefinedReference } from 'Components/UserDefinedReference/UserDefinedReference';
import { ICON_CLOSE, ICON_EDIT_MODIFY_UPDATE, ICON_EXPAND_COLLAPSE } from 'Config/Icons';
import { INHERENT_RISK_QUESTIONNAIRE, SERVICE_DASHBOARD, THIRD_PARTY_DOCUMENTS, UPDATE_THIRD_PARTY, UPDATE_THIRD_PARTY_SERVICE } from 'Config/PathTemplates';
import { ISSUES_EXCEPTIONS, TPRM } from 'Config/Paths';
import { TPRM_THIRD_PARTY_EIN, TPRM_THIRD_PARTY_MANAGER, TPRM_THIRD_PARTY_SERVICE_DELEGATES, TPRM_THIRD_PARTY_SERVICE_DESCRIPTION, TPRM_THIRD_PARTY_SERVICE_MANAGER, TPRM_THIRD_PARTY_SERVICE_RESPONSIBLE_ORGANIZATION } from 'Config/Tooltips';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { ellipt } from 'Helpers/StringUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { countOpenExceptions } from 'Models/Exceptions';
import { countOpenIssues } from 'Models/Issues';
import { FinalizationState, ServiceForNonTprmUser, ThirdPartyContact, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser } from 'Models/TPRM';
import { UserResponse } from 'Models/User';
import { ThirdPartyLocation } from 'Pages/TPRM/ServiceDashboard/ThirdPartyLocation/ThirdPartyLocation';

import styles from './ThirdPartiesAndServicesTableRow.module.css';
import { PrimaryTextAndSecondaryTextTableCell } from '../TableCells/PrimaryTextAndSecondaryTextTableCell/PrimaryTextAndSecondaryTextTableCell';
import { RiskRatingTableCell } from '../TableCells/RiskRatingTableCell/RiskRatingTableCell';
import { ServiceAssessmentProgressTableCell } from '../TableCells/ServiceAssessmentProgressTableCell/ServiceAssessmentProgressTableCell';
import { TextCell } from '../TableCells/TextCell/TextCell';
import sharedStyles from '../ThirdPartiesAndServicesListingSharedStyles.module.css';

const getCreatedString = (users: UserResponse[], created_time: string, createdByUserId?: string) => (created_time && createdByUserId ? `${iso8601ToUsDateShort(created_time)} by ${getUserNameFromSubject(createdByUserId, users, UserNameFormat.FIRST_SPACE_LAST)}` : iso8601ToUsDateShort(created_time));

export type ThirdPartiesAndServicesTableRowData =
    | {
          userHasTprmRole: true;
          userId: string;
          thirdParty: ThirdPartyResponseWithServices;
          serviceData?: {
              service: ThirdPartyService;
              serviceCompletedStepNumber: number;
          };
      }
    | {
          userHasTprmRole: false;
          userId: string;
          thirdParty: ThirdPartyWithServicesForNonTprmUser;
          serviceData?: {
              service: ServiceForNonTprmUser;
              serviceCompletedStepNumber: number;
          };
      };

export interface ThirdPartiesAndServicesTableRowProps {
    data: ThirdPartiesAndServicesTableRowData;
    columnSpan: number;
    onDisplayContacts: (contacts: ThirdPartyContact[], header: string) => void;
    onDisplayUsers: (userIds: string[], header: string) => void;
    onConfirmFinalizeService: (thirdParty: ThirdPartyResponseWithServices, service: ThirdPartyService) => void;
    onConfirmFinalizeThirdParty: (thirdParty: ThirdPartyResponseWithServices) => void;
    onConfirmSubmitServiceToBeFinalized: (thirdParty: ThirdPartyResponseWithServices | ThirdPartyWithServicesForNonTprmUser, service: ThirdPartyService | ServiceForNonTprmUser) => void;
    onConfirmSubmitThirdPartyToBeFinalized: (thirdParty: ThirdPartyResponseWithServices | ThirdPartyWithServicesForNonTprmUser) => void;
    onSetAssessmentDueDate: (service: ThirdPartyService) => void;
    onSetIrqTargetCompletionDate: (service: ThirdPartyService) => void;
}

export const ThirdPartiesAndServicesTableRow = (props: ThirdPartiesAndServicesTableRowProps) => {
    const navigate = useNavigate();
    const cachedData = useCachedData();

    const [open, setOpen] = useState(false);

    const directionStyle = open ? styles.down : styles.right;
    const ariaLabel = `${open ? 'collapse' : 'expand'} row for ${props.data.thirdParty.name}${props.data.serviceData?.service ? `: ${props.data.serviceData.service.name}` : ''}`;

    // The point of returning a discriminated union here is to avoid the need for `as` in the conditionals below that construct the overflow menu options.
    // `thirdParty` and `service` need to be "pulled back out", via `const { thirdParty, service } = calculatedInfo;`, in various spots below in order to capture the `service` object, which could technically become `undefined` between renders, to be used in callbacks.
    const calculatedInfo:
        | {
              userHasTprmRole: true;
              thirdParty: ThirdPartyResponseWithServices;
              service?: ThirdPartyService;
              thirdPartyIsADraft: boolean;
              serviceIsADraft: boolean;
              userHasOwnershipOfThirdParty: true;
              userHasOwnershipOfService: true;
              userCanFinalizeThirdParty: boolean;
              userCanFinalizeService: boolean;
          }
        | {
              userHasTprmRole: false;
              thirdParty: ThirdPartyWithServicesForNonTprmUser;
              service?: ServiceForNonTprmUser;
              thirdPartyIsADraft: boolean;
              serviceIsADraft: boolean;
              userHasOwnershipOfThirdParty: boolean;
              userHasOwnershipOfService: boolean;
              userCanFinalizeThirdParty: boolean;
              userCanFinalizeService: boolean;
          } = (() => {
        if (props.data.userHasTprmRole) {
            const thirdParty = props.data.thirdParty;
            const service = props.data.serviceData?.service;

            const thirdPartyIsADraft = thirdParty.finalization_state !== FinalizationState.FINALIZED;
            const serviceIsADraft = service !== undefined && service.finalization_state !== FinalizationState.FINALIZED;

            const userHasOwnershipOfThirdParty = true;
            const userHasOwnershipOfService = true;

            const userCanFinalizeThirdParty = thirdPartyIsADraft;
            const userCanFinalizeService = serviceIsADraft;

            return { userHasTprmRole: true, thirdParty, service, thirdPartyIsADraft, serviceIsADraft, userHasOwnershipOfThirdParty, userHasOwnershipOfService, userCanFinalizeThirdParty, userCanFinalizeService };
        } else {
            const thirdParty = props.data.thirdParty;
            const service = props.data.serviceData?.service;

            const thirdPartyIsADraft = thirdParty.finalization_state !== FinalizationState.FINALIZED;
            const serviceIsADraft = service !== undefined && service.finalization_state !== FinalizationState.FINALIZED;

            const userHasOwnershipOfThirdParty = thirdPartyIsADraft && thirdParty.created_by === props.data.userId;
            const userHasOwnershipOfService = serviceIsADraft && service.created_by === props.data.userId;

            const userCanFinalizeThirdParty = false;
            const userCanFinalizeService = false;

            return { userHasTprmRole: false, thirdParty, service, thirdPartyIsADraft, serviceIsADraft, userHasOwnershipOfThirdParty, userHasOwnershipOfService, userCanFinalizeThirdParty, userCanFinalizeService };
        }
    })();

    const overflowItems: OverflowItem[] = [];

    if (calculatedInfo.userHasOwnershipOfThirdParty) {
        overflowItems.push({
            text: 'Edit Third Party',
            onClickAction: () => navigate(generatePath(UPDATE_THIRD_PARTY, { thirdPartyId: calculatedInfo.thirdParty.id })),
            icon: ICON_EDIT_MODIFY_UPDATE,
        });
    }

    if (calculatedInfo.thirdParty.finalization_state === FinalizationState.DRAFT_IN_PROGRESS && calculatedInfo.userHasOwnershipOfThirdParty && !props.data.userHasTprmRole) {
        overflowItems.push({
            text: 'Submit Third Party to be Finalized',
            onClickAction: () => props.onConfirmSubmitThirdPartyToBeFinalized(calculatedInfo.thirdParty),
            icon: faCheck,
        });
    }

    if (calculatedInfo.userHasTprmRole && calculatedInfo.userCanFinalizeThirdParty) {
        overflowItems.push({
            text: 'Finalize Third Party',
            onClickAction: () => props.onConfirmFinalizeThirdParty(calculatedInfo.thirdParty),
            icon: faCheck,
        });
    }

    if (calculatedInfo.service && calculatedInfo.userHasOwnershipOfService) {
        const { thirdParty, service } = calculatedInfo;
        overflowItems.push({
            text: 'Edit Third-Party Service',
            onClickAction: () => navigate(generatePath(UPDATE_THIRD_PARTY_SERVICE, { thirdPartyId: thirdParty.id, serviceId: service.id })),
            icon: ICON_EDIT_MODIFY_UPDATE,
        });
    }

    if (calculatedInfo.service && calculatedInfo.service.finalization_state === FinalizationState.DRAFT_IN_PROGRESS && calculatedInfo.userHasOwnershipOfService && !props.data.userHasTprmRole) {
        const { thirdParty, service } = calculatedInfo;
        overflowItems.push({
            text: 'Submit Third-Party Service to be Finalized',
            onClickAction: () => props.onConfirmSubmitServiceToBeFinalized(thirdParty, service),
            icon: faCheck,
        });
    }

    if (calculatedInfo.userHasTprmRole && calculatedInfo.service && calculatedInfo.userCanFinalizeService) {
        const { thirdParty, service } = calculatedInfo;
        overflowItems.push({
            text: 'Finalize Third-Party Service',
            onClickAction: () => props.onConfirmFinalizeService(thirdParty, service),
            icon: faCheck,
        });
    }

    if (calculatedInfo.userHasTprmRole && calculatedInfo.service) {
        const { service } = calculatedInfo;
        overflowItems.push({
            text: 'Set Inherent Risk Questionnaire Target Completion Date',
            onClickAction: () => props.onSetIrqTargetCompletionDate(service),
            icon: faCalendar,
        });
    }

    if (calculatedInfo.service && calculatedInfo.serviceIsADraft && calculatedInfo.userHasOwnershipOfService) {
        const { thirdParty, service } = calculatedInfo;
        overflowItems.push({
            text: 'View/Edit Inherent Risk Questionnaire',
            onClickAction: () => navigate(generatePath(INHERENT_RISK_QUESTIONNAIRE, { third_party_id: thirdParty.id, service_id: service.id })),
            icon: ICON_EDIT_MODIFY_UPDATE,
        });
    }

    if (calculatedInfo.userHasTprmRole && calculatedInfo.service) {
        const { service } = calculatedInfo;
        overflowItems.push({
            text: 'Set Assessment Due Date',
            onClickAction: () => props.onSetAssessmentDueDate(service),
            icon: faCalendar,
        });
    }

    if (calculatedInfo.userHasTprmRole) {
        overflowItems.push({
            text: 'Manage Documents for Third Party',
            onClickAction: () => navigate(generatePath(THIRD_PARTY_DOCUMENTS, { third_party_id: calculatedInfo.thirdParty.id })),
            icon: faFolder,
        });
    }

    if (overflowItems.length === 0) {
        overflowItems.push({
            text: 'Close Menu (no actions available)',
            onClickAction: () => {},
            icon: ICON_CLOSE,
        });
    }

    const overflowMenuProps: OverflowMenuProps = {
        overflowItems: overflowItems,
        accessibilityTitle: calculatedInfo.service ? `Open menu for ${calculatedInfo.thirdParty.name}: ${calculatedInfo.service.name}` : `Open menu for ${calculatedInfo.thirdParty.name}`,
    };

    // TODO: These will potentially be repeated multiple times per vendor. Consider memoizing a Map.
    const getOpenIssuesLabel = (thirdParty: ThirdPartyResponseWithServices) => {
        const openIssues = countOpenIssues(thirdParty.issues);
        return openIssues === 1 ? '1 open issue' : `${openIssues} open issues`;
    };
    const getOpenExceptionsLabel = (thirdParty: ThirdPartyResponseWithServices) => {
        const openExceptions = countOpenExceptions(thirdParty.exceptions);
        return openExceptions === 1 ? '1 open exception' : `${openExceptions} open exceptions`;
    };

    const { thirdParty, service } = calculatedInfo;

    return (
        <>
            <TableRow>
                <TableCell className={sharedStyles.parentCell}>
                    <button type="button" onClick={() => setOpen(!open)} className={`${styles.icon} ${directionStyle}`} aria-label={`${ariaLabel}`}>
                        <FontAwesomeIcon icon={ICON_EXPAND_COLLAPSE} />
                    </button>
                </TableCell>
                {thirdParty.finalization_state === FinalizationState.FINALIZED ? <TextCell>{thirdParty.name}</TextCell> : <PrimaryTextAndSecondaryTextTableCell primaryText={thirdParty.name} secondaryText={thirdParty.finalization_state} />}
                {service ? (
                    service.finalization_state === FinalizationState.FINALIZED && calculatedInfo.userHasTprmRole ? (
                        <TableCell className={styles.parentCell}>
                            <Link size="sm" to={generatePath(SERVICE_DASHBOARD, { third_party_id: thirdParty.id, service_id: service.id })}>
                                {service.name}
                            </Link>
                        </TableCell>
                    ) : (
                        <PrimaryTextAndSecondaryTextTableCell primaryText={service.name} secondaryText={service.finalization_state} />
                    )
                ) : (
                    <TableCell className={sharedStyles.parentCell} />
                )}
                <ServiceAssessmentProgressTableCell rowData={props.data} />
                <TableCell className={sharedStyles.parentCell}>{service ? <RiskRatingTableCell riskRating={service?.inherent_risk_score} /> : <></>}</TableCell>
                <TableCell className={sharedStyles.parentCell}>{service ? <RiskRatingTableCell riskRating={service?.residual_risk_score} /> : <></>}</TableCell>
                <TextCell>{service?.assessment_workflow_setup.due_date ? iso8601ToUsDateShort(service.assessment_workflow_setup.due_date) : undefined}</TextCell>
                <TableOverflowCell className={sharedStyles.parentCell}>
                    <OverflowMenu {...overflowMenuProps} />
                </TableOverflowCell>
            </TableRow>
            <TableRow>
                <TableCell className={styles.childCell} />
                <TableCell colSpan={props.columnSpan} className={styles.childCell}>
                    <Collapse in={open} timeout="auto">
                        <div className={styles.collapsedInformationContainer}>
                            <div className={styles.collapsedThirdPartyInformation}>
                                <Text variant="Header3">{thirdParty.name}</Text>
                                <div className={styles.detailsContainer}>
                                    <div className={styles.detailsGridSection}>
                                        <div className={styles.detailsGridColumn}>
                                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                <FieldLabel fieldName="Manager" tooltipText={TPRM_THIRD_PARTY_MANAGER} />
                                                <Text>{getUserNameFromSubject(thirdParty.vendor_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) ?? ''}</Text>
                                            </div>
                                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                <FieldLabel fieldName="Employer ID Number" tooltipText={TPRM_THIRD_PARTY_EIN} />
                                                <Text>{thirdParty.ein ?? '-'}</Text>
                                            </div>
                                            {props.data.userHasTprmRole && (
                                                <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                    <FieldLabel fieldName="Issues" />
                                                    <Link size="sm" to={{ pathname: `/${TPRM}/${ISSUES_EXCEPTIONS}`, search: `thirdPartyId=${thirdParty.id}`, hash: '#issues' }}>
                                                        {getOpenIssuesLabel(props.data.thirdParty)}
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.detailsGridColumn}>
                                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                <FieldLabel fieldName="Created" />
                                                <Text>{getCreatedString(cachedData.users, thirdParty.created_time, thirdParty.created_by)}</Text>
                                            </div>
                                            <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                <FieldLabel fieldName="Contacts" />
                                                <Button variant="linkText" size="sm" onClick={() => props.onDisplayContacts(thirdParty.vendor_contacts, 'Third-Party Contacts')}>{`${thirdParty.vendor_contacts.length} ${thirdParty.vendor_contacts.length === 1 ? 'contact' : 'contacts'}`}</Button>
                                            </div>
                                            {props.data.userHasTprmRole && (
                                                <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                    <FieldLabel fieldName="Exceptions" />
                                                    <Link size="sm" to={{ pathname: `/${TPRM}/${ISSUES_EXCEPTIONS}`, search: `thirdPartyId=${thirdParty.id}`, hash: '#exceptions' }}>
                                                        {getOpenExceptionsLabel(props.data.thirdParty)}
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                        <FieldLabel fieldName="Website" />
                                        <UserDefinedReference reference={thirdParty.website} />
                                    </div>
                                    <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                        <FieldLabel fieldName="Location" />
                                        <ThirdPartyLocation thirdPartyMetadata={thirdParty} variant="nonDashboard" />
                                    </div>
                                    <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                        <FieldLabel fieldName="Tags" />
                                        {thirdParty.tags.length > 0 ? <TagListing tags={cachedData.convertTagIdsToDisplayableTags(thirdParty.tags)} showAll /> : <Text>-</Text>}
                                    </div>
                                </div>
                            </div>
                            {service && (
                                <>
                                    <div>
                                        <div className={styles.collapsedInformationSeparator} />
                                    </div>
                                    <div className={styles.collapsedServiceInformation}>
                                        <Text variant="Header3">{service.name}</Text>
                                        <div className={styles.detailsContainer}>
                                            <div className={styles.detailsGridSection}>
                                                <div className={styles.detailsGridColumn}>
                                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                        <FieldLabel fieldName="Manager" tooltipText={TPRM_THIRD_PARTY_SERVICE_MANAGER} />
                                                        <Text>{getUserNameFromSubject(service.vendor_service_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST) ?? ''}</Text>
                                                    </div>
                                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                        <FieldLabel fieldName="Last Assessed" />
                                                        <Text>{service.last_assessed ? iso8601ToUsDateShort(service.last_assessed) : '-'}</Text>
                                                    </div>
                                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                        <FieldLabel fieldName="Delegates" tooltipText={TPRM_THIRD_PARTY_SERVICE_DELEGATES} />
                                                        <Button variant="linkText" size="sm" onClick={() => props.onDisplayUsers(service.delegates, 'Delegates')}>{`${service.delegates.length} ${service.delegates.length === 1 ? 'delegate' : 'delegates'}`}</Button>
                                                    </div>
                                                </div>
                                                <div className={styles.detailsGridColumn}>
                                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                        <FieldLabel fieldName="Created" />
                                                        <Text>{getCreatedString(cachedData.users, service.created_time, service.created_by)}</Text>
                                                    </div>
                                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                        <FieldLabel fieldName="Responsible Organization" tooltipText={TPRM_THIRD_PARTY_SERVICE_RESPONSIBLE_ORGANIZATION} />
                                                        <Text>{service.responsible_organization ? ellipt(service.responsible_organization) : '-'}</Text>
                                                    </div>
                                                    <div className={styles.labelAndValueContainerInsideOfGrid}>
                                                        <FieldLabel fieldName="Contacts" />
                                                        <Button variant="linkText" size="sm" onClick={() => props.onDisplayContacts(service.vendor_contacts, 'Third-Party Service Contacts')}>{`${service.vendor_contacts.length} ${service.vendor_contacts.length === 1 ? 'contact' : 'contacts'}`}</Button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                                <FieldLabel fieldName="Inherent Risk Questionnaire Target Completion Date" />
                                                <Text>{service.assessment_workflow_setup.irq_target_completion_date ? iso8601ToUsDateShort(service.assessment_workflow_setup.irq_target_completion_date) : '-'}</Text>
                                            </div>
                                            <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                                <FieldLabel fieldName="Description" tooltipText={TPRM_THIRD_PARTY_SERVICE_DESCRIPTION} />
                                                <Text>{service.description}</Text>
                                            </div>
                                            <div className={styles.labelAndValueContainerOutsideOfGrid}>
                                                <FieldLabel fieldName="Tags" />
                                                {service.tags.length > 0 ? <TagListing tags={cachedData.convertTagIdsToDisplayableTags(service.tags)} showAll /> : <Text>-</Text>}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};
